import React, { useEffect, useState } from "react";
import { getDatabase, onValue, ref } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Info } from "lucide-react";
import "./UserTransportOrders.css";
import { db } from "../Firebase/firebase-config";

const UserTransportOrders = () => {
  const { currentAccount } = useAccount();
  const { userId: currentUserId } = useGetCurrentUserId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [podsData, setPodsData] = useState({});     // truckId -> POD
  const [invoicesData, setInvoicesData] = useState({}); // orderId -> invoices[]
  const [loading, setLoading] = useState(true);
  const [selectedDispatches, setSelectedDispatches] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showPageInfo, setShowPageInfo] = useState(false);

  const highlightOrderId = searchParams.get("orderId");
  const effectiveUid = currentAccount?.uid || currentUserId;

  useEffect(() => {
    if (!effectiveUid) return;

    const dbInstance = getDatabase();

    // Fetch Orders
    const ordersRef = ref(dbInstance, "transport_orders");
    const unsubOrders = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) return;

      const allOrders = [];
      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([orderId, order]) => {
          const isMyOrder =
            order.clientUid === effectiveUid ||
            order.clientId === effectiveUid ||
            (order.clientName || order.companyName) === currentAccount?.company_name;

          if (isMyOrder) {
            allOrders.push({ id: orderId, transporterId, ...order });
          }
        });
      });

      allOrders.sort((a, b) => (b.orderCreatedAt || 0) - (a.orderCreatedAt || 0));
      setOrders(allOrders);
    });

    // Fetch PODs & Invoices
    const docsRef = ref(dbInstance, "transport_documents");
    const unsubDocs = onValue(docsRef, (snapshot) => {
      if (!snapshot.exists()) {
        setLoading(false);
        return;
      }

      const allTransporters = snapshot.val();
   const podMap = {};      // orderId -> POD[]
const invoiceMap = {};  // orderId -> Invoice[]

Object.keys(allTransporters).forEach((transporterId) => {
  const transporterDocs = allTransporters[transporterId] || {};

  Object.keys(transporterDocs).forEach((orderId) => {
    const orderDocs = transporterDocs[orderId];

    // POD
    if (orderDocs?.POD) {
      Object.values(orderDocs.POD).forEach((pod) => {
        if (pod.clientId === effectiveUid) {
          if (!podMap[orderId]) {
            podMap[orderId] = [];
          }

          podMap[orderId].push(pod);
        }
      });
    }

    // Invoices
    if (orderDocs?.invoices) {
      Object.values(orderDocs.invoices).forEach((inv) => {
        if (!invoiceMap[orderId]) {
          invoiceMap[orderId] = [];
        }

        invoiceMap[orderId].push(inv);
      });
    }
  });
});

setPodsData(podMap);
setInvoicesData(invoiceMap);
      setLoading(false);
    });

    return () => {
      unsubOrders();
      unsubDocs();
    };
  }, [effectiveUid, currentAccount?.company_name]);

  const filteredOrders = orders.filter((order) => {
    const status = (order.status || "").toLowerCase();
    return !statusFilter || status === statusFilter.toLowerCase();
  });

  const statusOptions = [...new Set(orders.map((o) => o.status).filter(Boolean))];

  if (loading) {
    return <div className="tps-loading">Loading your transport orders...</div>;
  }

  return (
    <div className="tp-placeholder-content">
      <div className="tps-container">
        <div className="tps-header">
          <div className="ccr-topbar mb-1">
            <button className="ccr-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={18} />
              Back
            </button>

          </div>
          <div className="tps-header-top">

            <h1>Transport Orders</h1>
            <button
              onClick={() => setShowPageInfo(!showPageInfo)}
              className="tps-info-btn"
              title="Page information"
            >
              <Info size={20} />
            </button>
          </div>

          {showPageInfo && (
            <div className="tps-page-info-modal">
              <div className="tps-page-info-content">
                <button
                  onClick={() => setShowPageInfo(false)}
                  className="tps-info-close"
                >
                  ✕
                </button>
                <h3>Transport Orders Overview</h3>
                <ul>
                  <li>View all transport orders placed with transporters</li>
                  <li>Track order status (In-Process, Completed, etc.)</li>
                  <li>View dispatch history and Proof of Delivery and Invoices</li>
                  <li>Filter orders by client or status</li>
                  <li>Orders automatically highlight when viewed from quotes page</li>
                </ul>
              </div>
            </div>
          )}

          <div className="tps-filters">

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="tps-select"
            >
              <option value="">All Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table View - Large Screens */}
        <div className="tps-table-wrapper">
          <table className="tps-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order ID</th>
                <th>Quantity</th>
                <th>Route</th>
                <th>Status</th>
                <th>Transporter</th>
                <th>Phone</th>
                <th>Dispatches</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="10" className="tps-no-data">
                    No transport orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, index) => {
                  const isHighlighted =
                    (highlightOrderId &&
                      (order.orderId === highlightOrderId || order.id === highlightOrderId)) ||
                    order.status?.toLowerCase() === "in-process";
                  return (
                    <tr
                      key={order.id}
                      className={
                        highlightOrderId &&
                          (order.orderId === highlightOrderId || order.id === highlightOrderId)
                          ? "tps-row-highlight"
                          : order.status?.toLowerCase() === "in-process"
                            ? "tps-row-inprocess"
                            : ""
                      }
                    >
                      <td>{index + 1}</td>
                      <td className={isHighlighted ? "tps-cell-highlight" : ""}>{order.orderId || order.id || "-"}</td>
                      <td>
                        {order.quantity} {order.quantityUnit}
                      </td>
                      <td>
                        {order.from || "-"} → {order.to || "-"}
                      </td>
                      <td>
                        <span className="tps-status">
                          {order.status || "Pending"}
                        </span>
                      </td>
                      <td>{order.transporterName || "-"}</td>
                      <td>{order.transporterPhone || "-"}</td>
                      <td>
                        <button
                          className="tps-btn-view"
                          onClick={() =>
                            setSelectedDispatches({
                              ...(order.dispatch || {}),
                              orderId: order.orderId || order.id,
                            })
                          }
                        >
                          View
                        </button>
                      </td>
                      <td>
                        {new Date(order.orderCreatedAt || order.createdAt || Date.now()).toLocaleDateString()}
                      </td>
                      <td>
                        {new Date(order.lastUpdated || order.orderCreatedAt || Date.now()).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Card View - Small Screens */}
        <div className="tps-cards-wrapper">
          {filteredOrders.length === 0 ? (
            <div className="tps-no-data-cards">No transport orders found</div>
          ) : (
            filteredOrders.map((order, index) => {
              const isHighlighted = highlightOrderId && (order.orderId === highlightOrderId || order.id === highlightOrderId);
              return (
                <div
                  key={order.id}
                  className={`tps-card ${highlightOrderId &&
                      (order.orderId === highlightOrderId || order.id === highlightOrderId)
                      ? "tps-card-highlight"
                      : order.status?.toLowerCase() === "in-process"
                        ? "tps-card-inprocess"
                        : ""
                    }`}
                >
                  <div className="tps-card-header">
                    <div className="tps-card-title">Order #{index + 1}</div>
                    <span className="tps-status">{order.status || "Pending"}</span>
                  </div>

                  <div className="tps-card-body">
                    <div className="tps-card-row">
                      <span className="tps-card-label">Order ID:</span>
                      <span className="tps-card-value">{order.orderId || order.id || "-"}</span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Quantity:</span>
                      <span className="tps-card-value">
                        {order.quantity} {order.quantityUnit}
                      </span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Route:</span>
                      <span className="tps-card-value">
                        {order.from || "-"} → {order.to || "-"}
                      </span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Transporter:</span>
                      <span className="tps-card-value">{order.transporterName || "-"}</span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Phone:</span>
                      <span className="tps-card-value">{order.transporterPhone || "-"}</span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Created:</span>
                      <span className="tps-card-value">
                        {new Date(order.orderCreatedAt || order.createdAt || Date.now()).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="tps-card-row">
                      <span className="tps-card-label">Updated:</span>
                      <span className="tps-card-value">
                        {new Date(order.lastUpdated || order.orderCreatedAt || Date.now()).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="tps-card-footer">
                    <button
                      className="tps-btn-view tps-btn-full"
                      onClick={() =>
                        setSelectedDispatches({
                          ...(order.dispatch || {}),
                          orderId: order.orderId || order.id,
                        })
                      }
                    >
                      View Dispatches
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Dispatch Popup */}
        {/* Dispatch Popup */}
        {selectedDispatches && (
          <div className="tps-modal-overlay" onClick={() => setSelectedDispatches(null)}>
            <div className="tps-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tps-modal-header">
                <h3>Dispatch History - Order #{selectedDispatches.orderId}</h3>
                <button
                  className="tps-btn-close"
                  onClick={() => setSelectedDispatches(null)}
                >
                  ×
                </button>
              </div>

              {!selectedDispatches?.items ? (
                <p className="tps-no-dispatch">No dispatches assigned yet.</p>
              ) : (
                <>
                  <div className="tps-dispatch-summary">
                    {/* Keep your existing summary */}
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">From</div>
                      <div className="tps-summary-value">{selectedDispatches.from || "-"}</div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">To</div>
                      <div className="tps-summary-value">{selectedDispatches.to || "-"}</div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Order Qty</div>
                      <div className="tps-summary-value">{selectedDispatches.orderQuantity || "-"}</div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Total Load</div>
                      <div className="tps-summary-value">{selectedDispatches.totalLoad || "-"}</div>
                    </div>
                    <div className="tps-summary-item">
                      <div className="tps-summary-label">Order ID</div>
                      <div className="tps-summary-value">{selectedDispatches.orderId}</div>
                    </div>
                  </div>

                  <div className="tps-dispatch-table-wrapper">
                    <table className="tps-dispatch-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Truck No.</th>
                          <th>Driver No.</th>
                          <th>Load</th>
                          <th>Date & Time</th>
                          <th>Proof of Delivery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(selectedDispatches.items || {}).map((dispatch, index) => {

  const pod = (podsData[selectedDispatches.orderId] || []).find(
    (p) =>
      p.truckId === dispatch.truckId ||
      p.truckNumber === dispatch.truckNumber
  );

  return (
    <tr key={index}>
      <td>{index + 1}</td>
      <td>{dispatch.truckNumber || "-"}</td>
      <td>{dispatch.driverNumber || "-"}</td>
      <td>{dispatch.load || "-"} {dispatch.unit || ""}</td>
      <td>
        {dispatch.createdAt
          ? new Date(dispatch.createdAt).toLocaleString()
          : "-"}
      </td>
      <td>
        {pod ? (
          <a
            href={pod.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="tps-proof-link"
          >
            <Download size={16} /> View POD
          </a>
        ) : (
          <span className="tps-no-proof">No POD yet</span>
        )}
      </td>
    </tr>
  );
})}
                      </tbody>
                    </table>

                  </div>

                  {invoicesData[selectedDispatches.orderId]?.length > 0 && (
                    <div className="tps-documents-section">
                      <div className="tps-pod-box">
                        <div className="tps-section-header">
                          <h4>Invoices</h4>
                          <span className="invoice-count">
                            {invoicesData[selectedDispatches.orderId].length}
                          </span>
                        </div>

                        <div className="invoice-list">
                          {invoicesData[selectedDispatches.orderId].map((inv, i) => (
                            <a
                              key={i}
                              href={inv.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="invoice-card"
                            >
                              <div className="invoice-left">
                                <div className="invoice-icon">
                                  <Download size={18} />
                                </div>

                                <div className="invoice-details">
                                  <span className="invoice-name">
                                    {inv.fileName || `Invoice ${i + 1}`}
                                  </span>
                                  <span className="invoice-subtitle">
                                    Click to download
                                  </span>
                                </div>
                              </div>

                              <Download size={18} className="invoice-download-icon" />
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>

  );
};

export default UserTransportOrders;
